import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentViewer from './DocumentViewer';
import LoadingOverlay from './LoadingOverlay';
import './Demo.css';

const API_BASE = 'https://ai.griffso.com/api/parsing';
const DEMO_CLIENT = 'Guest_Demo';
const DEMO_KEY = 'Guest1234098675XXXXPPLL^$^%&7899';

// Master list — filtered at runtime by features returned from DB
const ALL_DOC_TYPES = [
  { value: 'PAN',     label: 'PAN Card',        icon: '🪪', feature: 'PAN',            accept: 'image/*' },
  { value: 'AADHAAR', label: 'Aadhaar Card',    icon: '🆔', feature: 'AADHAAR',        accept: 'image/*', dual: true },
  { value: 'DRIVING', label: 'Driving License', icon: '🚗', feature: 'DRIVING',        accept: 'image/*' },
  { value: 'VOTER',   label: 'Voter ID',        icon: '🗳️', feature: 'VOTER',          accept: 'image/*' },
  { value: 'PDF',     label: 'Resume / ATS',    icon: '📄', feature: 'RESUME',         accept: '.pdf', pdf: 'RESUME' },
  { value: 'PDF',     label: 'Tax Notice',      icon: '📋', feature: 'NOTICE',         accept: '.pdf', pdf: 'NOTICEAI' },
  { value: 'PDF',     label: 'AI Summary',      icon: '✨', feature: 'SUMMARY',        accept: '.pdf', pdf: 'SUMMARY' },
  { value: 'PDF',     label: 'Bank Statement',  icon: '🏦', feature: 'BANK_STATEMENT', accept: '.pdf', pdf: 'BANK_STATEMENT' },
];

export default function Demo() {
  const nav = useNavigate();

  const [token, setToken]         = useState('');
  const [features, setFeatures]   = useState([]);
  const [docTypes, setDocTypes]   = useState([]);   // built from DB features
  const [authErr, setAuthErr]     = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  const [selected, setSelected]   = useState(null);
  const [file, setFile]           = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile]   = useState(null);
  const [previewUrls, setPreviewUrls] = useState({ file: null, front: null, back: null });

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [results, setResults]     = useState(null);
  const [scrollY, setScrollY]     = useState(0);
  const [demoUsage, setDemoUsage] = useState({ used: 0, limit: 2, remaining: 2, allowed: true });
  const [mobileMenu, setMobileMenu] = useState(false);
  const [demoModal, setDemoModal]   = useState(false);

  const fileRef  = useRef();
  const frontRef = useRef();
  const backRef  = useRef();

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Check demo usage status on load
  useEffect(() => {
    fetch(`${API_BASE}/demo/status`)
      .then(r => r.json())
      .then(d => setDemoUsage(d))
      .catch(() => {});
  }, []);

  // Auto-auth as Guest_Demo — credentials & features come from DB
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Portal-Internal': '1' },
          body: JSON.stringify({ Authorization: DEMO_KEY, client_name: DEMO_CLIENT }),
        });
        const data = await res.json();
        if (res.ok) {
          const dbFeatures = data.features || [];
          setToken(data.access_token);
          setFeatures(dbFeatures);
          // Build doc type list from what DB says this client can access
          const allowed = ALL_DOC_TYPES.filter(dt => dbFeatures.includes(dt.feature));
          setDocTypes(allowed);
          setSelected(allowed[0] || null);
        } else {
          setAuthErr(data.error?.msg || 'Demo session failed to start');
          setDocTypes(ALL_DOC_TYPES);
          setSelected(ALL_DOC_TYPES[0]);
        }
      } catch (e) {
        setAuthErr('Cannot reach API server. Make sure it is running on port 5000.');
        setDocTypes(ALL_DOC_TYPES);
        setSelected(ALL_DOC_TYPES[0]);
      }
      setAuthLoading(false);
    })();
  }, []);

  const resetFiles = () => {
    setFile(null); setFrontFile(null); setBackFile(null);
    setPreviewUrls({ file: null, front: null, back: null });
    setResults(null); setError('');
  };


  const handleSelect = (dt) => { setSelected(dt); resetFiles(); };

  const setFileWithPreview = (f, type) => {
    if (!f) return;
    const url = f.type.startsWith('image/') ? URL.createObjectURL(f) : null;
    if (type === 'file')  { setFile(f);      setPreviewUrls(p => ({ ...p, file: url })); }
    if (type === 'front') { setFrontFile(f); setPreviewUrls(p => ({ ...p, front: URL.createObjectURL(f) })); }
    if (type === 'back')  { setBackFile(f);  setPreviewUrls(p => ({ ...p, back: URL.createObjectURL(f) })); }
  };

  const canProcess = () => {
    if (!token || !selected) return false;
    if (selected.dual) return !!(frontFile && backFile);
    return !!file;
  };

  const handleProcess = async () => {
    if (!canProcess()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      // Check + consume a demo slot
      const limitRes = await fetch(`${API_BASE}/demo/use`, { method: 'POST' });
      const limitData = await limitRes.json();
      if (!limitData.allowed) {
        setError(limitData.error?.msg || 'Demo limit reached for today.');
        setDemoUsage({ used: limitData.used, limit: limitData.limit, remaining: 0, allowed: false });
        setLoading(false);
        return;
      }
      setDemoUsage({ used: limitData.used, limit: limitData.limit, remaining: limitData.remaining, allowed: limitData.remaining > 0 });

      const fd = new FormData();
      fd.append('document_type', selected.value);
      if (selected.pdf) fd.append('pdf_category', selected.pdf);
      if (selected.dual) { fd.append('front', frontFile); fd.append('back', backFile); }
      else fd.append('file', file);

      const res = await fetch(`${API_BASE}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-Portal-Internal': '1' },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) setResults(data);
      else setError(data.error?.msg || 'Processing failed');

    } catch (e) {
      setError('Network error: ' + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="demo">
      {/* Mobile Menu */}
      <div className={`demo-mobile-menu ${mobileMenu ? 'open' : ''}`}>
        <button className="demo-mobile-menu-close" onClick={() => setMobileMenu(false)}>✕</button>
        <button onClick={() => { nav('/'); setMobileMenu(false); }}>← Back to Home</button>
        <div className="demo-mobile-menu-actions">
          <button className="demo-btn-ghost" onClick={() => { nav('/'); setMobileMenu(false); }}>← Back</button>
          <button className="demo-btn-primary" onClick={() => { nav('/parsingmodel'); setMobileMenu(false); }}>Client Login</button>
        </div>
      </div>

      {/* Orbs */}
      <div className="demo-orb demo-orb-1" style={{ transform: `translateY(${scrollY * 0.12}px)` }} />
      <div className="demo-orb demo-orb-2" style={{ transform: `translateY(${scrollY * -0.08}px)` }} />
      <div className="demo-orb demo-orb-3" style={{ transform: `translateY(${scrollY * 0.06}px)` }} />

      {/* Navbar */}
      <nav className={`demo-nav ${scrollY > 40 ? 'scrolled' : ''}`}>
        <div className="demo-nav-inner">
          <div className="demo-logo" onClick={() => nav('/')}>
            <span>🚀</span>
            <span className="demo-logo-text">Griffso<span className="demo-logo-ai">AI</span></span>
          </div>
          <div className="demo-nav-badge">🧪 Live Demo — Guest Session</div>
          <div className="demo-nav-actions">
            <button className="demo-btn-ghost" onClick={() => nav('/')}>← Back</button>
            <button className="demo-btn-primary" onClick={() => nav('/parsingmodel')}>Client Login</button>
          </div>
          <button className="demo-hamburger" onClick={() => setMobileMenu(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className="demo-body">
        {/* Hero */}
        <div className="demo-hero">
          <span className="demo-hero-badge">🎯 Interactive Demo</span>
          <h1>Try Griffso<span className="demo-gradient">AI</span> Live</h1>
          <p>Upload any supported document and see real AI extraction in seconds. No login required.</p>
          {authLoading && <div className="demo-auth-status loading">⏳ Starting demo session…</div>}
          {!authLoading && !authErr && token && (
            <div className="demo-auth-status ok">
              ✅ Demo session active — {features.length} features unlocked
            </div>
          )}
          {authErr && <div className="demo-auth-status err">⚠️ {authErr}</div>}
          {!authLoading && token && (
            <div className={`demo-usage-badge ${demoUsage.remaining === 0 ? 'exhausted' : demoUsage.remaining === 1 ? 'warn' : ''}`}>
              {demoUsage.remaining === 0
                ? <>🚫 Daily limit reached — <strong>{demoUsage.limit} scans/day</strong>. Come back tomorrow!</>
                : <>🎯 <strong>{demoUsage.remaining}</strong> of {demoUsage.limit} free scans remaining today</>
              }
            </div>
          )}
        </div>

        {/* Doc Type Cards */}
        <div className="demo-doctype-row">
          <div className="demo-doctype-label">Select Document Type</div>
          <div className="demo-doctype-cards">
            {authLoading
              ? <div className="demo-sidebar-loading"><span className="demo-spinner-sm" /> Loading…</div>
              : docTypes.map((dt, i) => (
                <button
                  key={i}
                  className={`demo-doctype-card ${selected === dt ? 'active' : ''}`}
                  onClick={() => handleSelect(dt)}
                >
                  <span className="demo-doctype-card-icon">{dt.icon}</span>
                  <span className="demo-doctype-card-label">{dt.label}</span>
                </button>
              ))
            }
          </div>
        </div>

        {/* Main Panel */}
        <div className="demo-panel">
          <div className="demo-main">

            {/* Upload card */}
            {selected && (
              <div className="demo-upload-card">
                <div className="demo-upload-header">
                  <span className="demo-upload-icon">{selected.icon}</span>
                  <div>
                    <h3>{selected.label}</h3>
                    <p>
                      {selected.dual
                        ? 'Upload front & back images'
                        : selected.value === 'PDF'
                          ? 'Upload PDF file'
                          : 'Upload image file (PNG, JPG, JPEG)'}
                    </p>
                  </div>
                </div>

                {selected.dual ? (
                  <div className="demo-dual-upload">
                    {['front', 'back'].map(side => {
                      const f   = side === 'front' ? frontFile : backFile;
                      const ref = side === 'front' ? frontRef  : backRef;
                      const url = side === 'front' ? previewUrls.front : previewUrls.back;
                      return (
                        <div key={side} className="demo-upload-zone" onClick={() => ref.current?.click()}>
                          <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => setFileWithPreview(e.target.files[0], side)} />
                          {url
                            ? <img src={url} alt={side} className="demo-preview-img" />
                            : <>
                                <span className="demo-zone-icon">📁</span>
                                <span className="demo-zone-label">{side === 'front' ? 'Front Side' : 'Back Side'}</span>
                                <span className="demo-zone-hint">Click to upload</span>
                              </>
                          }
                          {f && <div className="demo-file-name">{f.name}</div>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="demo-upload-zone single" onClick={() => fileRef.current?.click()}>
                    <input ref={fileRef} type="file" accept={selected.accept} style={{ display: 'none' }}
                      onChange={e => setFileWithPreview(e.target.files[0], 'file')} />
                    {previewUrls.file
                      ? <img src={previewUrls.file} alt="preview" className="demo-preview-img" />
                      : <>
                          <span className="demo-zone-icon">{selected.value === 'PDF' ? '📄' : '🖼️'}</span>
                          <span className="demo-zone-label">Drop file here or click to browse</span>
                          <span className="demo-zone-hint">
                            {selected.accept === '.pdf' ? 'PDF only' : 'PNG, JPG, JPEG'} · Max 16MB
                          </span>
                        </>
                    }
                    {file && !previewUrls.file && (
                      <div className="demo-pdf-info">
                        <span className="demo-pdf-icon">📄</span>
                        <span className="demo-pdf-name">{file.name}</span>
                        <span className="demo-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                      </div>
                    )}
                    {file && <div className="demo-file-name">{file.name}</div>}
                  </div>
                )}

                {error && (
                  <div className="demo-error">
                    <span>⚠️</span>
                    <span>{error}</span>
                    <button onClick={() => setError('')}>✕</button>
                  </div>
                )}

                <button
                  className="demo-process-btn"
                  onClick={handleProcess}
                  disabled={!canProcess() || loading || authLoading || !demoUsage.allowed}
                >
                  {loading
                    ? <><span className="demo-spinner" /> Analyzing…</>
                    : <><span>🤖</span> Analyze Document</>
                  }
                </button>

                {!token && !authLoading && (
                  <div className="demo-warn">🔒 Waiting for demo session…</div>
                )}
              </div>
            )}

            {/* Results */}
            {results && (
              <div className="demo-results-wrap">
                <DocumentViewer
                  results={results}
                  documentType={selected.value}
                  pdfCategory={selected.pdf || ''}
                  previewUrls={previewUrls}
                  file={file}
                  frontFile={frontFile}
                  backFile={backFile}
                />
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="demo-cta">
          <div className="demo-cta-inner">
            <span className="demo-cta-badge">🚀 Ready to go further?</span>
            <h2>Get Full Access for Your Business</h2>
            <p>
              The demo runs on a shared guest account. Get your own tenant with dedicated API keys,
              usage analytics, and custom feature access.
            </p>
            <div className="demo-cta-actions">
              <button className="demo-btn-hero" onClick={() => setDemoModal(true)}>
                📅 Schedule a Demo
              </button>
              <button className="demo-btn-hero-outline" onClick={() => nav('/parsingmodel')}>
                Client Login →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay — fixed full-screen, outside demo-body */}
      {loading && (
        <LoadingOverlay
          documentType={selected?.value}
          pdfCategory={selected?.pdf || ''}
        />
      )}

      {demoModal && <ScheduleDemoModal onClose={() => setDemoModal(false)} />}
    </div>
  );
}

function ScheduleDemoModal({ onClose }) {
  const [form, setForm]     = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch('https://ai.griffso.com/api/ai_model/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, subject: 'Schedule a Demo', source: 'Griffso Demo' }),
      });
      const data = await res.json();
      if (res.ok && data.success) setStatus('success');
      else { setErrMsg(data.error || 'Something went wrong'); setStatus('error'); }
    } catch {
      setErrMsg('Network error. Please try again.'); setStatus('error');
    }
  };

  return (
    <div className="sdm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sdm-modal">
        <button className="sdm-close" onClick={onClose}>✕</button>

        {status === 'success' ? (
          <div className="sdm-success">
            <span className="sdm-success-icon">🎉</span>
            <h3>Request Submitted!</h3>
            <p>We'll reach out to you shortly to schedule your demo.</p>
            <button className="demo-btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="sdm-header">
              <span>📅</span>
              <div>
                <h3>Schedule a Demo</h3>
                <p>Fill in your details and we'll get back to you within 24 hours.</p>
              </div>
            </div>

            <form className="sdm-form" onSubmit={handleSubmit}>
              <div className="sdm-row">
                <div className="sdm-field">
                  <label>Name *</label>
                  <input required placeholder="Your full name" value={form.name}
                    onChange={e => set('name', e.target.value)} />
                </div>
                <div className="sdm-field">
                  <label>Email *</label>
                  <input required type="email" placeholder="you@company.com" value={form.email}
                    onChange={e => set('email', e.target.value)} />
                </div>
              </div>
              <div className="sdm-field">
                <label>Phone</label>
                <input placeholder="+91 98765 43210" value={form.phone}
                  onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="sdm-field">
                <label>Message *</label>
                <textarea required rows={4} placeholder="Tell us about your use case…" value={form.message}
                  onChange={e => set('message', e.target.value)} />
              </div>

              {status === 'error' && (
                <div className="demo-error" style={{ marginTop: 0 }}>
                  <span>⚠️</span><span>{errMsg}</span>
                  <button type="button" onClick={() => setStatus('idle')}>✕</button>
                </div>
              )}

              <button className="demo-process-btn" type="submit" disabled={status === 'loading'}>
                {status === 'loading'
                  ? <><span className="demo-spinner" /> Submitting…</>
                  : <><span>🚀</span> Submit Request</>
                }
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
